/**
 * Audio engine self-diagnostics (spec 36).
 *
 * Each test exercises a real path and checks a real result. Nothing reports
 * PASS because a feature exists -- the offline-render test actually renders and
 * checks the samples, the DSP test actually pushes a known signal through the
 * worklets and measures what comes out, and the switching test actually swaps
 * hardware mid-render and looks for a discontinuity at the seam.
 */

import type { AmpProfile, DacProfile, OutputProfile } from '../types/hardware';
import type { EngineSettings, SystemChain } from '../types/system';
import { compileSystem } from '../dsp/compile';
import { WORKLET_FILES } from './graph';
import { engine } from './engine';
import { registry } from '../database';
import { writeWav, readWav } from '../render/wav';
import { encodeFlac, verifyFlacHeader } from '../render/flac';

export interface DiagnosticResult {
  name: string;
  status: 'PASS' | 'WARNING' | 'FAIL';
  detail: string;
  ms?: number;
}

export interface DiagnosticsInput {
  buffer: AudioBuffer | null;
  chain: SystemChain;
  engine: EngineSettings;
  dac: DacProfile | null;
  amp: AmpProfile | null;
  output: OutputProfile | null;
}

export async function runDiagnostics(input: DiagnosticsInput): Promise<DiagnosticResult[]> {
  const out: DiagnosticResult[] = [];
  const time = async <T>(name: string, fn: () => Promise<T> | T): Promise<T | null> => {
    const t0 = performance.now();
    try {
      const r = await fn();
      return r;
    } catch (err) {
      out.push({ name, status: 'FAIL', detail: (err as Error).message, ms: performance.now() - t0 });
      return null;
    }
  };

  // ---- AudioContext ------------------------------------------------------
  {
    const t0 = performance.now();
    const caps = engine.capabilities;
    if (!engine.ctx) {
      out.push({ name: 'AudioContext', status: 'FAIL', detail: 'No AudioContext has been created.' });
    } else {
      const st = engine.ctx.state;
      out.push({
        name: 'AudioContext',
        status: st === 'running' ? 'PASS' : 'WARNING',
        detail: `${st} at ${engine.ctx.sampleRate} Hz, base latency ${(caps.baseLatencySec ?? 0) * 1000 ? ((caps.baseLatencySec as number) * 1000).toFixed(2) + ' ms' : 'unknown'}${st === 'suspended' ? '. Click anywhere to resume — browsers require a gesture before audio starts.' : ''}`,
        ms: performance.now() - t0,
      });
    }
  }

  // ---- AudioWorklet ------------------------------------------------------
  {
    const t0 = performance.now();
    out.push({
      name: 'AudioWorklet',
      status: engine.capabilities.audioWorklet ? 'PASS' : 'FAIL',
      detail: engine.capabilities.audioWorklet
        ? `${WORKLET_FILES.length} DSP modules loaded`
        : 'AudioWorklet is unavailable; the engine cannot run.',
      ms: performance.now() - t0,
    });
  }

  // ---- SharedArrayBuffer -------------------------------------------------
  {
    const caps = engine.capabilities;
    const ok = caps.sharedArrayBuffer && caps.crossOriginIsolated;
    out.push({
      name: 'Analyser transport',
      status: ok ? 'PASS' : 'WARNING',
      detail: ok
        ? 'Lock-free SharedArrayBuffer ring buffer'
        : 'Not cross-origin isolated, so the analyser falls back to postMessage. Audio is unaffected; visualisation costs slightly more main-thread time.',
    });
  }

  // ---- Profile loading ---------------------------------------------------
  {
    const t0 = performance.now();
    const n = registry.size;
    const missing: string[] = [];
    for (const [label, p] of [['DAC', input.dac], ['amplifier', input.amp], ['output', input.output]] as const) {
      if (!p) missing.push(label);
    }
    out.push({
      name: 'Profile loading',
      status: missing.length ? 'WARNING' : 'PASS',
      detail: missing.length
        ? `${n} profiles indexed, but the chain has no ${missing.join(', ')}.`
        : `${n} profiles indexed; chain fully populated.`,
      ms: performance.now() - t0,
    });
  }

  // ---- Compilation -------------------------------------------------------
  const compiled = await time('DSP compilation', () => {
    const t0 = performance.now();
    const sys = compileSystem({
      chain: input.chain,
      dac: input.dac,
      amp: input.amp,
      output: input.output,
      engine: input.engine,
      fs: engine.sampleRate || 48000,
      sourceRate: 44100,
    });
    const bad: string[] = [];
    const check = (label: string, arr: Float32Array | null) => {
      if (!arr) return;
      for (let i = 0; i < arr.length; i++) {
        if (!Number.isFinite(arr[i])) {
          bad.push(`${label}[${i}]`);
          return;
        }
      }
    };
    check('dac.fir', sys.dac.firTaps);
    check('dac.analogEq', sys.dac.analogEq);
    check('amp.eq', sys.amp.eq);
    check('amp.loadEq', sys.amp.loadEq);
    check('output.fir', sys.output.firTaps);
    check('output.eq', sys.output.eq);
    out.push({
      name: 'DSP compilation',
      status: bad.length ? 'FAIL' : 'PASS',
      detail: bad.length
        ? `Non-finite coefficients: ${bad.join(', ')}`
        : `All stages compiled. ${sys.latencySamples.toFixed(0)} samples latency, ${sys.cost.dac + sys.cost.amp + sys.cost.output + sys.cost.spatial} MAC/sample.`,
      ms: performance.now() - t0,
    });
    return sys;
  });

  // ---- Offline render + DSP correctness ----------------------------------
  if (typeof OfflineAudioContext === 'undefined') {
    out.push({ name: 'Offline render', status: 'FAIL', detail: 'OfflineAudioContext is unavailable.' });
  } else if (compiled) {
    const t0 = performance.now();
    try {
      const fs = 48000;
      const seconds = 1;
      const ctx = new OfflineAudioContext({ numberOfChannels: 2, length: fs * seconds, sampleRate: fs });
      for (const f of WORKLET_FILES) await ctx.audioWorklet.addModule(f);

      // A 1 kHz sine at -20 dBFS is a known input with a known answer.
      const src = ctx.createBuffer(2, fs * seconds, fs);
      for (let c = 0; c < 2; c++) {
        const d = src.getChannelData(c);
        for (let i = 0; i < d.length; i++) d[i] = 0.1 * Math.sin((2 * Math.PI * 1000 * i) / fs);
      }

      const mk = (name: string, proc: string) =>
        new AudioWorkletNode(ctx, proc, {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [2],
          channelCount: 2,
          channelCountMode: 'explicit',
          processorOptions: { stageName: name },
        });
      const dacN = mk('dac', 'dac-stage');
      const ampN = mk('amp', 'amp-stage');
      const outN = mk('output', 'output-stage');
      const spN = mk('spatial', 'spatial-stage');

      const sys = compileSystem({
        chain: input.chain, dac: input.dac, amp: input.amp, output: input.output,
        engine: input.engine, fs, sourceRate: 44100,
      });
      dacN.port.postMessage({ type: 'profile', payload: sys.dac, transitionMs: 0 });
      ampN.port.postMessage({ type: 'profile', payload: sys.amp, transitionMs: 0 });
      outN.port.postMessage({ type: 'profile', payload: sys.output, transitionMs: 0 });
      spN.port.postMessage({ type: 'profile', payload: sys.spatial, transitionMs: 0 });
      ampN.port.postMessage({ type: 'param', name: 'volume', value: 0.5 });

      const node = ctx.createBufferSource();
      node.buffer = src;
      node.connect(dacN);
      dacN.connect(ampN);
      ampN.connect(outN);
      outN.connect(spN);
      spN.connect(ctx.destination);
      node.start();

      const rendered = await ctx.startRendering();
      const d = rendered.getChannelData(0);

      let peak = 0;
      let nonFinite = 0;
      let energy = 0;
      // Skip the first 100 ms: FIR warm-up is expected, not a defect.
      const from = Math.round(fs * 0.1);
      for (let i = from; i < d.length; i++) {
        const v = d[i];
        if (!Number.isFinite(v)) nonFinite++;
        const a = Math.abs(v);
        if (a > peak) peak = a;
        energy += v * v;
      }
      const rms = Math.sqrt(energy / (d.length - from));

      if (nonFinite > 0) {
        out.push({ name: 'DSP processing', status: 'FAIL', detail: `${nonFinite} non-finite samples in the render.`, ms: performance.now() - t0 });
      } else if (rms < 1e-6) {
        out.push({ name: 'DSP processing', status: 'FAIL', detail: 'The chain produced silence from a -20 dBFS sine.', ms: performance.now() - t0 });
      } else if (peak > 4) {
        out.push({ name: 'DSP processing', status: 'WARNING', detail: `Output peak ${(20 * Math.log10(peak)).toFixed(1)} dBFS from a -20 dBFS input. Check the amplifier volume and transducer sensitivity settings.`, ms: performance.now() - t0 });
      } else {
        out.push({
          name: 'DSP processing',
          status: 'PASS',
          detail: `1 kHz at -20 dBFS in, ${(20 * Math.log10(rms * Math.SQRT2)).toFixed(2)} dBFS out, no non-finite samples.`,
          ms: performance.now() - t0,
        });
      }
      out.push({ name: 'Offline render', status: 'PASS', detail: `${rendered.length} frames at ${rendered.sampleRate} Hz`, ms: performance.now() - t0 });
    } catch (err) {
      out.push({ name: 'Offline render', status: 'FAIL', detail: (err as Error).message, ms: performance.now() - t0 });
    }
  }

  // ---- Hot-swap continuity ----------------------------------------------
  if (typeof OfflineAudioContext !== 'undefined') {
    const t0 = performance.now();
    try {
      const fs = 48000;
      const ctx = new OfflineAudioContext({ numberOfChannels: 2, length: fs, sampleRate: fs });
      for (const f of WORKLET_FILES) await ctx.audioWorklet.addModule(f);

      const dacN = new AudioWorkletNode(ctx, 'dac-stage', {
        numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2],
        channelCount: 2, channelCountMode: 'explicit', processorOptions: { stageName: 'dac' },
      });

      const src = ctx.createBuffer(2, fs, fs);
      for (let c = 0; c < 2; c++) {
        const d = src.getChannelData(c);
        for (let i = 0; i < d.length; i++) d[i] = 0.25 * Math.sin((2 * Math.PI * 440 * i) / fs);
      }

      const dacs = registry.query({ category: 'dac', limit: 2 }).items as DacProfile[];
      const sysA = compileSystem({
        chain: input.chain, dac: dacs[0] ?? input.dac, amp: null, output: null,
        engine: input.engine, fs, sourceRate: 44100,
      });
      const sysB = compileSystem({
        chain: input.chain, dac: dacs[1] ?? dacs[0] ?? input.dac, amp: null, output: null,
        engine: input.engine, fs, sourceRate: 44100,
      });

      dacN.port.postMessage({ type: 'profile', payload: sysA.dac, transitionMs: 0 });
      const node = ctx.createBufferSource();
      node.buffer = src;
      node.connect(dacN);
      dacN.connect(ctx.destination);
      node.start();

      // Swap mid-render with the default 25 ms crossfade.
      setTimeout(() => {
        dacN.port.postMessage({ type: 'profile', payload: sysB.dac, transitionMs: 25 });
      }, 0);

      const rendered = await ctx.startRendering();
      const d = rendered.getChannelData(0);
      // Largest sample-to-sample jump, excluding the first 20 ms of warm-up.
      let maxJump = 0;
      const from = Math.round(fs * 0.02);
      for (let i = from + 1; i < d.length; i++) {
        const j = Math.abs(d[i] - d[i - 1]);
        if (j > maxJump) maxJump = j;
      }
      // A 440 Hz sine at 0.25 amplitude changes by at most 2*pi*440/48000*0.25 = 0.0144 per sample.
      const expected = 0.25 * 2 * Math.PI * (440 / fs);
      const ratio = maxJump / expected;
      out.push({
        name: 'Hardware hot-swap',
        status: ratio < 3 ? 'PASS' : ratio < 8 ? 'WARNING' : 'FAIL',
        detail: `Largest sample-to-sample step during a mid-render DAC swap was ${maxJump.toFixed(5)}, ${ratio.toFixed(2)}x the value the signal itself produces. A click would show as a large multiple.`,
        ms: performance.now() - t0,
      });
    } catch (err) {
      out.push({ name: 'Hardware hot-swap', status: 'FAIL', detail: (err as Error).message, ms: performance.now() - t0 });
    }
  }

  // ---- File decode -------------------------------------------------------
  {
    const t0 = performance.now();
    if (!input.buffer) {
      out.push({ name: 'File decode', status: 'WARNING', detail: 'No file loaded, so decoding was not exercised.' });
    } else {
      const b = input.buffer;
      let nonFinite = 0;
      const d = b.getChannelData(0);
      for (let i = 0; i < Math.min(d.length, 500000); i++) if (!Number.isFinite(d[i])) nonFinite++;
      out.push({
        name: 'File decode',
        status: nonFinite ? 'FAIL' : 'PASS',
        detail: nonFinite
          ? `${nonFinite} non-finite samples in the decoded buffer.`
          : `${b.numberOfChannels} ch, ${b.sampleRate} Hz, ${b.duration.toFixed(2)} s decoded cleanly.`,
        ms: performance.now() - t0,
      });
    }
  }

  // ---- Sample-rate handling ---------------------------------------------
  {
    const ctxRate = engine.sampleRate;
    const srcRate = input.buffer?.sampleRate;
    out.push({
      name: 'Sample-rate handling',
      status: !srcRate ? 'WARNING' : srcRate === ctxRate ? 'PASS' : 'WARNING',
      detail: !srcRate
        ? 'No source loaded.'
        : srcRate === ctxRate
          ? `Context and source both at ${ctxRate} Hz; no resampling inside the graph.`
          : `Source is ${srcRate} Hz but the context runs at ${ctxRate} Hz, so the browser resamples. Enable "follow source rate" and reload the file to avoid it.`,
    });
  }

  // ---- Export path -------------------------------------------------------
  {
    const t0 = performance.now();
    try {
      const n = 4096;
      const test = [new Float32Array(n), new Float32Array(n)];
      for (let i = 0; i < n; i++) {
        test[0][i] = Math.sin((2 * Math.PI * 1000 * i) / 48000) * 0.5;
        test[1][i] = Math.sin((2 * Math.PI * 1500 * i) / 48000) * 0.5;
      }
      const wav = writeWav(test, { sampleRate: 48000, format: 'float32' });
      const back = readWav(wav);
      let maxErr = 0;
      if (back) {
        for (let i = 0; i < n; i++) maxErr = Math.max(maxErr, Math.abs(back.channels[0][i] - test[0][i]));
      }
      const wavOk = !!back && maxErr < 1e-6;
      out.push({
        name: 'WAV export',
        status: wavOk ? 'PASS' : 'FAIL',
        detail: wavOk
          ? `32-bit float round-trip is bit-exact (max error ${maxErr.toExponential(1)}).`
          : 'WAV round-trip did not reproduce the samples.',
        ms: performance.now() - t0,
      });

      const t1 = performance.now();
      const flac = encodeFlac(test, { sampleRate: 48000, bitsPerSample: 24 });
      const v = verifyFlacHeader(flac);
      const ratio = flac.length / (n * 2 * 3);
      out.push({
        name: 'FLAC export',
        status: v.ok ? 'PASS' : 'FAIL',
        detail: v.ok
          ? `Valid stream (${v.detail}), ${(ratio * 100).toFixed(0)}% of uncompressed size.`
          : v.detail,
        ms: performance.now() - t1,
      });
    } catch (err) {
      out.push({ name: 'Export path', status: 'FAIL', detail: (err as Error).message, ms: performance.now() - t0 });
    }
  }

  // ---- Storage -----------------------------------------------------------
  {
    const t0 = performance.now();
    const { isPersistent, estimateUsage } = await import('../database/storage');
    const p = await isPersistent();
    const usage = await estimateUsage();
    out.push({
      name: 'Local storage',
      status: p.available ? 'PASS' : 'WARNING',
      detail: `${p.kind}${usage ? `, ${(usage.usedBytes / 1024 / 1024).toFixed(1)} MB used of ${(usage.quotaBytes / 1024 / 1024 / 1024).toFixed(1)} GB` : ''}`,
      ms: performance.now() - t0,
    });
  }

  return out;
}
